import { Observe } from "./_/observe";
import gsap, { reduced, resetInitial } from "../gsap";
import { App } from "../app";

/**
 * Reveal Module
 *
 * Reveals content using clip-path polygon, with a subtle scale effect on the image.
 * You provide the clip container yourself so you control what gets revealed (e.g. image + overlay).
 *
 * Usage:
 * <figure data-module="reveal" data-direction="vertical">
 *   <div data-reveal-clip class="relative w-full">
 *     <img src="..." alt="...">
 *     <div class="absolute inset-0 bg-brand-blue/80">...</div>
 *   </div>
 * </figure>
 *
 * Attributes:
 * - data-direction: "vertical" (default, top to bottom) or "horizontal" (left to right)
 * - data-delay: Animation delay in seconds
 * - data-scale: Initial scale of the image (default 1.1)
 * - data-duration: Animation duration in seconds (default 1.0)
 */
export class Reveal extends Observe {
  #anim;
  #img;
  #clip;
  #overlay;
  #hasParallax = false;
  #direction = "vertical"; // "vertical" or "horizontal"

  a = {
    duration: 1.5,
    delay: 0,
    scale: 1.1,
  };

  constructor(element) {
    super(element, { threshold: 0.1, once: true, rootMargin: "0px 0px 200px 0px" });

    this.#clip = element.querySelector("[data-reveal-clip]");
    if (!this.#clip) {
      console.warn("Reveal module: no [data-reveal-clip] container found inside", element);
      return;
    }

    this.#img = this.#clip.querySelector("img");
    if (!this.#img) {
      console.warn("Reveal module: no img found inside [data-reveal-clip]", element);
      return;
    }

    // Find overlay element for mobile fade-out
    this.#overlay = this.#clip.querySelector("[data-overlay]");

    // When parallax is nested inside, it owns the img's scale transform.
    // Skip counter-zoom to avoid a hard scale jump after the reveal completes.
    this.#hasParallax = !!this.#clip.querySelector("[data-module='parallax']");

    const direction = element.dataset.direction;
    if (direction === "horizontal") this.#direction = "horizontal";

    const delay = element.dataset.delay;
    if (delay != null) this.a.delay = parseFloat(delay);

    const scale = element.dataset.scale;
    if (scale != null) this.a.scale = parseFloat(scale);

    const duration = element.dataset.duration;
    if (duration != null) this.a.duration = parseFloat(duration);

    this.create();
    resetInitial(element);
  }

  create() {
    if (reduced || !this.#img || !this.#clip) return;

    const initialClip = this.#direction === "horizontal"
      ? "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)"
      : "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)";

    gsap.set(this.#clip, {
      clipPath: initialClip,
    });

    if (!this.#hasParallax) {
      gsap.set(this.#img, { scale: this.a.scale });
    }
  }

  isIn = () => {
    if (reduced || !this.#img || !this.#clip) return;

    this.#anim = gsap.timeline();

    this.#anim.to(
      this.#clip,
      {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        duration: this.a.duration,
        delay: this.a.delay,
        ease: "power3.out",
      },
      0
    );

    if (!this.#hasParallax) {
      this.#anim.to(
        this.#img,
        {
          scale: 1,
          duration: this.a.duration,
          delay: this.a.delay,
          ease: "power3.out",
        },
        0
      );
    }

    // Fade out overlay on mobile after reveal completes
    if (App.isMobile && this.#overlay) {
      this.#anim.to(
        this.#overlay,
        {
          opacity: 0,
          duration: 0.5,
          delay: 0.7,
          ease: "power2.out",
        },
        0
      );
    }
  };

  transitionOut() {
    if (this.#anim) this.#anim.kill();
    this.destroy();
  }
}
